import React, { useState } from 'react'
import { Download, Upload, FileText, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

// Componentes UI Globales
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'

// Servicios y Store
import { useAuthStore } from '@/app/store/auth/auth.store'
import { inventoryService } from '@/app/services/inventory.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
interface ImportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ─── 2. Componente Principal ───────────────────────────────────────────────
export function ImportModal({ open, onOpenChange }: ImportModalProps) {
  // ─── Estados (Variables Reactivas) ───
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  
  // Store Global
  const { activeBranchId } = useAuthStore()

  // ─── Funciones de Interfaz ───
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const validExtensions = ['.xls', '.xlsx']
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      
      if (!validExtensions.includes(extension)) {
        toast.error('Formato inválido. Por favor selecciona un archivo Excel (.xls o .xlsx).')
        e.target.value = ''
        return
      }
      setSelectedFile(file)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const handleDownloadTemplate = () => {
    toast.info('Plantilla no disponible aún', {
      description: 'Esta funcionalidad estará lista en una próxima actualización.'
    })
  }

  // ─── Funciones API ───
  const handleImport = async () => {
    if (!selectedFile) return
    if (!activeBranchId) {
      toast.error('No hay una sucursal activa seleccionada.')
      return
    }

    setIsUploading(true)
    try {
      await inventoryService.importProducts(selectedFile, activeBranchId)
      toast.success('Procesando en segundo plano', {
        description: 'La importación de productos ha comenzado correctamente.'
      })
      onOpenChange(false)
      setSelectedFile(null)
    } catch (error) {
      toast.error('Error al iniciar la importación', {
        description: 'Ocurrió un problema al subir el archivo.'
      })
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (isUploading) return
      onOpenChange(val)
      if (!val) setSelectedFile(null)
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Importar Productos desde Excel</DialogTitle>
          <DialogDescription className="sr-only">
            Cargar productos masivamente desde un archivo Excel
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="mb-2 text-sm font-medium text-green-900">
              Instrucciones:
            </p>
            <ol className="list-inside list-decimal space-y-1 text-sm text-green-800">
              <li>Descarga la plantilla de Excel</li>
              <li>Completa los datos de los productos</li>
              <li>Sube el archivo completo</li>
            </ol>
          </div>

          <Button variant="outline" className="w-full" onClick={handleDownloadTemplate} disabled={isUploading}>
            <Download className="mr-2 h-4 w-4" />
            Descargar Plantilla Excel
          </Button>

          <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              id="excel-upload"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            {selectedFile ? (
              <div className="flex flex-col items-center justify-center space-y-2">
                <FileText className="h-12 w-12 text-blue-500" />
                <div className="flex flex-col items-center w-full px-4">
                  <p className="text-sm font-medium text-slate-700 break-all text-center" title={selectedFile.name}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => document.getElementById('excel-upload')?.click()}
                    disabled={isUploading}
                    className="text-slate-600 hover:text-slate-800"
                  >
                    Reemplazar archivo
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={isUploading}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <label htmlFor="excel-upload" className="cursor-pointer block w-full">
                <Upload className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                <p className="text-sm text-slate-600">
                  Haz clic para seleccionar archivo
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Solo archivos .xlsx o .xls
                </p>
              </label>
            )}
          </div>

          <Button 
            className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d]" 
            onClick={handleImport} 
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Iniciando importación...
              </>
            ) : (
              'Importar Productos'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
